import classNames from 'classnames/bind';
import { addMinutes } from 'date-fns';
import { usePaymentReceiptHandler } from '../../../hooks/usePaymentReceiptHandler';
import { useAppSelector } from '../../../store/hooks';
import { selectUserInformation } from '../../../store/reducers';
import { Header } from '../../organisms';
import { BookFirstAppointment } from './StageElements/BookFirstAppointment';
import { BookFollowUpAppointment } from './StageElements/BookFollowUpAppointment';
import { FirstAppointmentBooked } from './StageElements/FirstAppointmentBooked';
import { FollowUpAppointmentBooked } from './StageElements/FollowUpAppointmentBooked';
import { FollowUpPrescription } from './StageElements/FollowUpPrescription';
import { MedicationInfo } from './StageElements/MedicationInfo';
import { PatientDeclined } from './StageElements/PatientDeclined';
import { PatientNotSuitable } from './StageElements/PatientNotSuitable';
import { PaymentStatusPoller } from './StageElements/PaymentStatusPoller';
import { PharmacyInfo } from './StageElements/PharmacyInfo';
import { PrescriptionInfo } from './StageElements/PrescriptionInfo';
import { Questionnaire } from './StageElements/Questionnaire';
import { StayTuned } from './StageElements/StayTuned';
import { UnpaidPayments } from './StageElements/UnpaidPayments';
import { WaitingForApproval } from './StageElements/WatingForApproval';
import { Faq } from './Faq';
import style from './Dashboard.module.scss';

const cx = classNames.bind(style);

export function Dashboard(): JSX.Element {
  const userInformation = useAppSelector(selectUserInformation);
  const hasUnpaidPayment = userInformation?.hasUnpaidPayment;
  // needed conditions:
  // 1. Questionnaire not finished
  // 2. Checking Questionnaire
  // 3. Book First Appointment
  // 3.1. First Appointment booked
  // 3.2. First Appointment took place -> awaiting Status from Doctor
  // 4. Book Follow Up Appointment
  // 4.1. Follow Up Appointment booked
  // 4.2. Follow Up Appointment took place -> awaiting Status from Doctor
  // 5. Request Follow Up Prescription (condition: at least 2 appointments took place)
  // ----
  // 6. Patient is declined / blocked

  // PaymentReceipt is set in sessionStorage when the user is redirected from the payment provider
  const { status, paymentId, clearStatus } = usePaymentReceiptHandler();

  const isPaymentProcessing = status === 'PROCESSING' && Boolean(paymentId);

  // clean up appointments -> remove excused and cancelled and paymentExpired
  const filteredPatientAppointments = userInformation?.appointment?.filter(
    ({ carriedOut }) => carriedOut !== 2 && carriedOut !== 3 && carriedOut !== 4
  );

  const delayForStayTunedAfterAppointment = 10; // in minutes
  // True if latest appointment is more than 10 Minutes ago and no status is set by the doctor
  const showStayTuned =
    userInformation?.progress.isDataConfirm &&
    filteredPatientAppointments?.length > 0 &&
    filteredPatientAppointments[filteredPatientAppointments.length - 1].carriedOut === undefined &&
    new Date() >
      addMinutes(
        new Date(filteredPatientAppointments[filteredPatientAppointments.length - 1]?.endDate),
        delayForStayTunedAfterAppointment
      );
  const isPatientDeclined = userInformation?.isPatientDeclined && userInformation.isPatientDeclined === true;
  const isPatientNotSuitable =
    userInformation?.isPatientNotSuitable !== undefined && userInformation.isPatientNotSuitable === true;

  const canBookAppointments =
    userInformation.progress.isDataConfirm === true &&
    userInformation.patientDataApproved === true &&
    !isPaymentProcessing &&
    !hasUnpaidPayment;
  const requiresFirstAppointment = userInformation?.requiresFirstAppointment === true;

  const latestAppointment = filteredPatientAppointments?.[filteredPatientAppointments.length - 1];
  const isLatestAppointmentBooked =
    latestAppointment?.carriedOut === undefined || latestAppointment?.carriedOut === null;
  const isLatestAppointmentFirstAppointment =
    String(latestAppointment?.appointmentType) === 'firstAppointment' ||
    String(latestAppointment?.appointmentType) === '1';

  const medicationPharmacyInfoClasses = cx({ MedicationPharmacyInfo: true });
  const currentStepContainerClasses = cx({ CurrentStepContainer: true }, 'fullWidthSectionInContainer');

  return (
    <>
      <Header showBackButton={false} />

      <div>
        <div>
          {status && paymentId && <PaymentStatusPoller paymentId={paymentId} onComplete={clearStatus} />}
          {userInformation.id !== '' && (
            <div className={currentStepContainerClasses}>
              {/*
                Condition for 3.2: First Appointment took place -> awaiting Status from Doctor
                Condition for 4.2: Follow Up Appointment took place -> awaiting Status from Doctor
    */}
              {showStayTuned === true && <StayTuned filteredPatientAppointments={filteredPatientAppointments} />}
              {/*
                Condition for 6: Patient is declined / blocked
        */}
              {isPatientDeclined === true && <PatientDeclined />}
              {isPatientNotSuitable === true && <PatientNotSuitable />}

              {showStayTuned === false && isPatientDeclined === false && isPatientNotSuitable === false && (
                <>
                  {/*
                Condition for 1: Questionnaire not finished
        */}
                  {userInformation.progress.isDataConfirm === false && <Questionnaire />}
                  {/*
                Condition for 2: Checking Questionnaire
        */}
                  {userInformation.progress.isDataConfirm === true && userInformation.patientDataApproved !== true && (
                    <WaitingForApproval />
                  )}
                  {/*
                Condition for 3: Book First Appointment
        */}
                  {canBookAppointments &&
                    (filteredPatientAppointments?.length === 0 ||
                      !filteredPatientAppointments ||
                      (requiresFirstAppointment === true &&
                        !(isLatestAppointmentBooked && isLatestAppointmentFirstAppointment))) && (
                      <BookFirstAppointment requiresFirstAppointment={requiresFirstAppointment} />
                    )}
                  {/*
                Condition for 3.1: First Appointment booked
        */}
                  {userInformation.progress.isDataConfirm === true &&
                    userInformation.patientDataApproved === true &&
                    (filteredPatientAppointments?.length === 1 ||
                      (isLatestAppointmentBooked && isLatestAppointmentFirstAppointment)) &&
                    !isPaymentProcessing &&
                    new Date() <
                      addMinutes(new Date(latestAppointment?.endDate), delayForStayTunedAfterAppointment) && (
                      <FirstAppointmentBooked appointmentInformation={latestAppointment} />
                    )}
                  {/*
                Condition for 4: Book Follow Up Appointment
        */}
                  {canBookAppointments &&
                    filteredPatientAppointments?.length >= 1 &&
                    filteredPatientAppointments[filteredPatientAppointments.length - 1].carriedOut === 1 &&
                    !requiresFirstAppointment &&
                    !isPaymentProcessing && (
                      <BookFollowUpAppointment
                        allAppointments={filteredPatientAppointments}
                        userInformation={userInformation}
                      />
                    )}
                  {/*
                  Condition for 4.1: Follow Up Appointment booked
          */}
                  {userInformation.progress.isDataConfirm === true &&
                    userInformation.patientDataApproved === true &&
                    filteredPatientAppointments?.length > 1 &&
                    (!requiresFirstAppointment ||
                      (isLatestAppointmentBooked && !isLatestAppointmentFirstAppointment)) &&
                    !isPaymentProcessing &&
                    new Date() <
                      addMinutes(new Date(latestAppointment?.endDate), delayForStayTunedAfterAppointment) && (
                      <FollowUpAppointmentBooked appointmentInformation={latestAppointment} />
                    )}
                  {/*
                Condition for 5: Request Follow Up Prescription (condition: at least 1 appointment took place and they have had medication before)
        */}
                  {filteredPatientAppointments?.length >= 1 &&
                    userInformation.medication?.length > 0 &&
                    !isPaymentProcessing &&
                    !hasUnpaidPayment && <FollowUpPrescription userInformation={userInformation} />}

                  {/*
                Condition for 6: Remind user for unpaid payments (condition: hasUnpaidPayment is true)
        */}
                  {hasUnpaidPayment && !isPaymentProcessing && <UnpaidPayments />}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {userInformation.id !== '' && isPatientDeclined === false && isPatientNotSuitable === false && (
        <>
          <div className={medicationPharmacyInfoClasses}>
            {userInformation.medication?.length > 0 && <MedicationInfo medication={userInformation.medication} />}
            {(userInformation.patientPharmacyRelation || filteredPatientAppointments?.length === 1) && (
              <PharmacyInfo pharmacy={userInformation.patientPharmacyRelation} />
            )}
          </div>
          {userInformation.prescription && userInformation.prescription.length > 0 && (
            <div className={medicationPharmacyInfoClasses}>
              <PrescriptionInfo prescriptions={userInformation.prescription} />
            </div>
          )}
        </>
      )}
      <Faq />
    </>
  );
}
